import { useRef, useMemo } from 'react';
import ColorLegend from './ColorLegend';
import OrganizerContacts from './OrganizerContacts';
import FieldHeadersRow from './FieldHeadersRow';
import TimelineFields from './TimelineFields';
import Field from './Field';
import { useFirebase } from './FirebaseContextProvider';
import { scheduleStartHour, scheduleStepMin } from '../utils/consts';
import './SchedulePrintView.scss';

const noop = () => {};

export default function SchedulePrintView({
  tournamentId,
  date,
  sortedFields,
  filteredGames,
  colorLegend,
  timeSlots,
}) {
  const { tournamentsById } = useFirebase();
  const wrapRef = useRef(null);

  const tournament = tournamentsById.get(tournamentId);

  const trimmedTimeSlots = useMemo(() => {
    if (!filteredGames.length) return timeSlots.slice(0, 24);

    let maxEndMin = 0;
    for (const game of filteredGames) {
      if (!game.start) continue;
      const [h, m] = game.start.split(':').map(Number);
      const endMin = h * 60 + m + (game.duration || 0);
      if (endMin > maxEndMin) maxEndMin = endMin;
    }

    if (!maxEndMin) return timeSlots;

    const slotCount =
      Math.ceil((maxEndMin - scheduleStartHour * 60) / scheduleStepMin) + 1;
    return timeSlots.slice(0, slotCount);
  }, [filteredGames, timeSlots]);

  if (!tournament) return null;

  const renderFields = (fieldsList) =>
    fieldsList.map((f) => (
      <Field
        key={f.id}
        id={tournamentId}
        field={f.id}
        timeSlots={trimmedTimeSlots}
        games={filteredGames}
        colorLegend={colorLegend}
        setBracketsPopup={noop}
        setGame={noop}
        highlightedGameId={null}
        onSearchTermClick={noop}
        onSearchHoverChange={noop}
        activeSearchHoverKey={null}
      />
    ));

  return (
    <div className='schedule-print-view'>
      <header className='print-header'>
        <div className='logo bg-image wh-64' />
        <div className='name-date'>
          <h1>{tournament.name}</h1>
          <p className='print-date'>{date.format('dddd, MMMM D, YYYY')}</p>
        </div>
        <OrganizerContacts org={tournament.organizations?.[0]} print />
      </header>
      <ColorLegend id={tournamentId} colorLegend={colorLegend} />
      <FieldHeadersRow fields={sortedFields} scrollRef={wrapRef} />
      <TimelineFields
        wrapRef={wrapRef}
        timeSlots={trimmedTimeSlots}
        sortedFields={sortedFields}
        fieldFilter={null}
        renderFields={renderFields}
        spinner={false}
      />
    </div>
  );
}
